import React from 'react';
import { ArrowLeft, Cookie, Shield, Database, Settings, Clock, Mail } from 'lucide-react';
import './FooterPages.css';

const CookiePolicy = ({ onBack }) => {
  return (
    <div className="footer-page">
      <div className="footer-page-container">
        <button className="back-button" onClick={onBack}>
          <ArrowLeft size={18} />
          Back
        </button>

        <div className="footer-page-header">
          <div className="footer-page-icon cookie-icon">
            <Cookie size={32} />
          </div>
          <h1>Cookie Policy</h1>
          <p>Last updated: January 2026</p>
        </div>

        <div className="footer-page-content">
          <section className="policy-section">
            <h2>
              <Cookie size={20} />
              What Are Cookies?
            </h2>
            <p>
              Cookies are small text files that are stored on your device when you visit a website. 
              They help websites remember your preferences and improve your experience.
            </p>
          </section>

          <section className="policy-section">
            <h2>
              <Database size={20} />
              How We Use Cookies
            </h2>
            <p>
              The Research Methodology Wizard uses cookies and similar technologies (localStorage) 
              for the following purposes:
            </p>
            <div className="policy-list">
              <div className="policy-item">
                <strong>Essential Functionality</strong>
                <p>To save your research design progress, wizard selections, and preferences so you don't lose your work between sessions.</p>
              </div>
              <div className="policy-item">
                <strong>Authentication</strong>
                <p>To keep you signed in to your account and remember your subscription status.</p>
              </div>
              <div className="policy-item">
                <strong>User Preferences</strong>
                <p>To remember your display settings, language preferences, and other customizations.</p>
              </div>
              <div className="policy-item">
                <strong>Saved Designs</strong>
                <p>To store your saved research designs locally so you can access them later.</p>
              </div>
            </div>
          </section>

          <section className="policy-section">
            <h2>
              <Shield size={20} />
              Types of Data Stored
            </h2>
            <p>We store the following types of data locally on your device:</p>
            <ul className="bullet-list">
              <li>Wizard progress and selections (Steps 0-10 for each wizard)</li>
              <li>Saved research design configurations</li>
              <li>User authentication tokens (if logged in)</li>
              <li>Subscription tier information</li>
              <li>Cookie consent preferences</li>
              <li>UI preferences (e.g., collapsed panels)</li>
            </ul>
          </section>

          <section className="policy-section">
            <h2>
              <Settings size={20} />
              Your Choices
            </h2>
            <div className="choice-cards">
              <div className="choice-card accept">
                <h4>If You Accept Cookies</h4>
                <p>
                  Your progress will be saved automatically. You can pick up where you left off 
                  when you return. Your saved designs will be preserved.
                </p>
              </div>
              <div className="choice-card reject">
                <h4>If You Reject Cookies</h4>
                <p>
                  The app will still function, but your progress won't be saved between sessions. 
                  You'll need to start fresh each time you visit.
                </p>
              </div>
            </div>
          </section>

          <section className="policy-section">
            <h2>
              <Clock size={20} />
              How Long Data Is Stored
            </h2>
            <p>
              Data stored in your browser's localStorage persists until you clear your browser 
              data or delete specific items. There is no automatic expiration.
            </p>
            <p>
              You can clear your stored data at any time by:
            </p>
            <ul className="bullet-list">
              <li>Clearing your browser's cache and local storage</li>
              <li>Using your browser's developer tools to remove specific items</li>
              <li>Deleting saved designs through the Design Manager</li>
            </ul>
          </section>

          <section className="policy-section">
            <h2>
              <Shield size={20} />
              Third-Party Cookies
            </h2>
            <p>
              We do not use third-party tracking cookies or analytics services that track your 
              behavior across other websites. All data is stored locally on your device and is 
              not transmitted to external servers for tracking purposes.
            </p>
          </section>

          <section className="policy-section">
            <h2>
              <Settings size={20} />
              Changing Your Cookie Preferences
            </h2>
            <p>
              You can change your cookie preferences at any time by clearing your browser's 
              local storage. The cookie consent banner will appear again on your next visit.
            </p>
            <p>
              To clear localStorage in most browsers:
            </p>
            <ol className="numbered-list">
              <li>Open Developer Tools (F12 or right-click → Inspect)</li>
              <li>Go to the Application or Storage tab</li>
              <li>Find Local Storage in the sidebar</li>
              <li>Right-click and select "Clear" or delete specific items</li>
            </ol>
          </section>

          <section className="policy-section">
            <h2>
              <Mail size={20} />
              Contact Us
            </h2>
            <p>
              If you have questions about this Cookie Policy, please contact us at:
            </p>
            <p className="contact-email">
              <a href="mailto:researchmethodologywizard@gmail.com">
                researchmethodologywizard@gmail.com
              </a>
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default CookiePolicy;
